import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { translations } from './translations';
import type { Language, TranslationKeys } from './types';
import { RTL_LANGUAGES } from './types';
import { supabase } from '@/integrations/supabase/client';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'sayitnow-language';

const SUPPORTED_PREFIXES: [string, Language][] = [
  ['ar', 'ar'], ['es', 'es'], ['pt', 'pt'], ['fr', 'fr'],
  ['ja', 'ja'], ['ko', 'ko'], ['id', 'id'], ['ms', 'id'],
  ['hi', 'hi'],
];

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language.toLowerCase();
  for (const [prefix, lang] of SUPPORTED_PREFIXES) {
    if (browserLang.startsWith(prefix)) return lang;
  }
  return 'en';
}

function isValidLanguage(val: string | null | undefined): val is Language {
  return !!val && val in translations;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isValidLanguage(stored)) {
        return stored;
      }
    }
    return detectBrowserLanguage();
  });

  const isRTL = RTL_LANGUAGES.includes(language);

  // Sync language preference from profile on auth change
  useEffect(() => {
    let cancelled = false;

    const syncFromProfile = async (userId: string) => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('language_preference')
          .eq('user_id', userId)
          .maybeSingle();

        if (!cancelled && data?.language_preference && isValidLanguage(data.language_preference)) {
          // Profile preference takes precedence over localStorage for logged-in users
          setLanguageState(data.language_preference);
          localStorage.setItem(STORAGE_KEY, data.language_preference);
        }
      } catch {
        // Silently ignore — fall back to localStorage / browser detection
      }
    };

    // Listen for auth changes
    const { data: { subscription } } = (supabase.auth as any).onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user?.id) {
          syncFromProfile(session.user.id);
        } else if (event === 'INITIAL_SESSION' && session?.user?.id) {
          // Only sync from profile on INITIAL_SESSION if no local preference exists
          const stored = localStorage.getItem(STORAGE_KEY);
          if (!stored) {
            syncFromProfile(session.user.id);
          }
        }
      }
    );

    // Also check current session on mount — only if no local preference
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      (supabase.auth as any).getSession().then(({ data: { session } }: any) => {
        if (session?.user?.id) {
          syncFromProfile(session.user.id);
        }
      });
    }

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Persist language change to localStorage + profile
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);

    // Fire-and-forget profile update for logged-in users
    (supabase.auth as any).getSession().then(({ data: { session } }: any) => {
      if (session?.user?.id) {
        supabase
          .from('profiles')
          .update({ language_preference: lang })
          .eq('user_id', session.user.id)
          .then(); // fire-and-forget
      }
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [language, isRTL]);

  const t: TranslationKeys = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
